import React, { useContext } from "react";
import {
  Grid,
  Button,
  Popover,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import styled, { css } from "styled-components";
import { observer, useObserver } from "mobx-react";
import { ContainerStoreContext } from "../../ContainerStore";
import DropdownRowColoring from "./DropdownRowColoring.js";
import RowAndCellColoring from "./RowAndCellColoring.js";
import { cloneDeep } from "lodash";
import { useRef } from "react";

function ColorPopup({ anchorEl, onClose, isOpen, coloringType }) {
  const { list, listId, view, containerStore, colorValues } = useData();
  const [rowColors, setRowColors] = React.useState([]);
  const [columnColors, setColumnColors] = React.useState([]);
  const [
    rowColorsBasedOnDropdown,
    setRowColorsBasedOnDropdown,
  ] = React.useState({});
  const [columnsForCriteriaPanel, setColumnsForCriteriaPanel] = React.useState(
    []
  );
  const [dropdownColumns, setDropdownColumns] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);

  const handleColorButtonSave = async (deleteColor = false) => {
    let colorCodes = [];
    let remainingColorCodes =
      coloringType === "dropdown" || coloringType === "row"
        ? columnColors
        : rowColors.length > 0
        ? rowColors
        : rowColorsBasedOnDropdown?.column_id
        ? [rowColorsBasedOnDropdown]
        : [];
    if (deleteColor === true) {
      colorCodes = [...remainingColorCodes];
    } else {
      colorCodes =
        coloringType === "dropdown" && rowColorsBasedOnDropdown?.column_id
          ? [rowColorsBasedOnDropdown, ...remainingColorCodes]
          : coloringType === "column"
          ? [...columnColors, ...remainingColorCodes]
          : [...rowColors, ...remainingColorCodes];
    }
    setIsSaving(true);
    await containerStore.saveColorCriteria(colorCodes);
    setIsSaving(false);
    onClose();
  };

  const handleAddColor = () => {
    let colorValue = {
      color: "",
      bg_color: "#fff",
      column_id: coloringType === "row" ? "" : [],
      criteria: {
        query_string: "",
        query_tree: [],
      },
      is_dropdown_row_coloring: 0,
      index: Math.random(),
    };
    if (coloringType === "row") {
      setRowColors([...rowColors, colorValue]);
    } else if (coloringType === "column") {
      setColumnColors([...columnColors, colorValue]);
    }
  };

  const getColors = (colorCodes) => {
    return colorCodes.map((colorCode) => {
      return {
        color: colorCode.color,
        bg_color: colorCode.bg_color,
        column_id: colorCode.column_id,
        is_dropdown_row_coloring: colorCode.is_dropdown_row_coloring,
        criteria: {
          query_string: colorCode.criteria,
          query_tree: colorCode.criteria_tree,
        },
        index: Math.random(),
      };
    });
  };

  const setColorCodes = async () => {
    if (
      colorValues.length === 0 ||
      (typeof colorValues === "object" && Object.keys(colorValues).length === 0)
    ) {
      return;
    }
    const data = Object.values(colorValues).flat();
    let rowColorCodes = data.filter((colorCode) => colorCode.column_id === "0"),
      columnColorCodes = data.filter(
        (colorCode) =>
          colorCode.column_id != "0" &&
          colorCode.is_dropdown_row_coloring === "0"
      ),
      rowColorCodesBasedOnDropdown = data.filter(
        (colorCode) =>
          colorCode.column_id != "0" &&
          colorCode.is_dropdown_row_coloring === "1"
      );

    if (rowColorCodes.length > 0) {
      setRowColors(getColors(rowColorCodes));
    }
    if (columnColorCodes.length > 0) {
      const columnColorsGrouped = {};
      columnColorCodes.forEach((columnColor) => {
        const uniqueKey = columnColor.criteria + columnColor.bg_color;
        if (!columnColorsGrouped[uniqueKey]) {
          columnColorsGrouped[uniqueKey] = {
            color: columnColor.color,
            bg_color: columnColor.bg_color,
            column_id: [columnColor.column_id],
            is_dropdown_row_coloring: columnColor.is_dropdown_row_coloring,
            criteria: {
              query_string: columnColor.criteria,
              query_tree: columnColor.criteria_tree,
            },
            index: Math.random(),
          };
        } else {
          columnColorsGrouped[uniqueKey].column_id.push(columnColor.column_id);
        }
      });
      setColumnColors(Object.values(columnColorsGrouped));
    }
    if (rowColorCodesBasedOnDropdown.length > 0) {
      setRowColorsBasedOnDropdown(getColors(rowColorCodesBasedOnDropdown)[0]);
    }
  };

  React.useEffect(() => {
    TL.list.reports.ColumnsFormatter.getViewColumns(
      list,
      view,
      Ext.bind(function (columns) {
        let currentSheetColumns = TL.list.settings.lookup.CriteriaHelper.formatColumns(
          cloneDeep(columns),
          {
            listNameStr: list.name,
          }
        );
        currentSheetColumns = currentSheetColumns.filter((item) => {
          return (
            item.name !== "ID" &&
            item.id !== "created_at" &&
            item.id !== "updated_at"
          );
        });
        let dropdownColumnsForColoring = currentSheetColumns.filter((item) => {
          return item.column_type_id === "3" && !item.name.includes("=>");
        });
        setDropdownColumns(dropdownColumnsForColoring);
        setColumnsForCriteriaPanel(currentSheetColumns);
      })
    );
    if (anchorEl && coloringType && coloringType !== "" && isOpen === true) {
      if (
        (rowColors.length == 0 && coloringType === "row") ||
        (columnColors.length == 0 && coloringType === "column")
      ) {
        handleAddColor();
      }
      setColorCodes();
    }
    return () => {
      setRowColors([]);
      setColumnColors([]);
      setRowColorsBasedOnDropdown({});
    };
  }, [list, view, isOpen]);

  const addColorScroller = useRef(null);

  return (
    <PopoverStyled
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      open={isOpen}
      onClose={onClose}
      anchorEl={anchorEl}
      maxWidth="xl"
      coloringType={coloringType}
    >
      <StyledDialogContent>
        {coloringType !== "dropdown" && (
          <>
            <RowAndCellColoring
              colorValues={coloringType === "row" ? rowColors : columnColors}
              coloringType={coloringType}
              columnsForCriteriaPanel={columnsForCriteriaPanel}
              listId={listId}
              setColorValues={
                coloringType === "row" ? setRowColors : setColumnColors
              }
              handleColorButtonSave={handleColorButtonSave}
            />
            <div className="addColor" ref={addColorScroller}></div>
          </>
        )}
        {coloringType === "dropdown" && (
          <DropdownRowColoring
            setRowColorsBasedOnDropdown={setRowColorsBasedOnDropdown}
            handleColorButtonSave={handleColorButtonSave}
            dropdownColumns={dropdownColumns}
            rowColorsBasedOnDropdown={rowColorsBasedOnDropdown}
          />
        )}
      </StyledDialogContent>
      <DialogActions>
        {coloringType !== "dropdown" && (
          <>
            <Button
              onClick={() => {
                handleAddColor();
                setTimeout(() => {
                  addColorScroller?.current?.scrollIntoView({
                    behaviour: "smooth",
                    block: "end",
                  });
                }, 120);
              }}
              color="primary"
            >
              + Add Color
            </Button>
            <StyledDiv></StyledDiv>
          </>
        )}
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleColorButtonSave();
          }}
          color="primary"
          disabled={isSaving}
        >
          Save {isSaving && <CircularProgress size={18} color="inherit" />}
        </Button>
      </DialogActions>
    </PopoverStyled>
  );
}

const PopoverStyled = styled(({ className, ...props }) => (
  <Popover {...props} classes={{ paper: className }} />
))`
  && {
    width: 100%;
    ${(props) =>
      props.coloringType &&
      css`
        max-width: ${props.coloringType === "dropdown" ? "500px" : "800px"};
      `}
  }
`;

const StyledDialogContent = styled(DialogContent)`
  max-height: 53vh;
  margin-top: 10px;
  background-color: #f9f9f9;
  padding: 0px !important;
`;

const StyledDiv = styled.div`
  flex: 1 0 0;
`;

function useData() {
  const containerStore = useContext(ContainerStoreContext);
  const currentListId = containerStore.listId;
  const listConfig = containerStore.listConfig;
  const currentViewStore = containerStore.getCurrentViewStore();

  return useObserver(() => ({
    list: listConfig.list,
    listId: currentListId,
    view: containerStore.view,
    containerStore: containerStore,
    colorValues: listConfig.colorCodes,
  }));
}

export default observer(ColorPopup);
